import { NzMarks, NzSliderComponent } from 'ng-zorro-antd/slider';
// @ts-ignore
import isInteger from 'lodash/isInteger';

import {
  Component,
  Input,
  OnInit,
  forwardRef,
  SimpleChanges,
  ViewChild,
  HostListener,
  ElementRef,
  OnChanges,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/**
 * Takes value & roundingFactor as input - returns value rounded to the nearest roundingFactor
 *
 *  **Example:**
 *
 *    Input
 *      5.1234, 0.1
 *      5.1234, 1
 *
 *    Output
 *    -> 5.1
 *    -> 5
 *
 */
export function roundWithRespectToSliderStep(value: number, roundingFactor: number): number | undefined {
  if (!value) return undefined;
  if (isInteger(roundingFactor)) return Math.round(value / roundingFactor) * roundingFactor;

  const numberOfFractionalDigits: number = roundingFactor.toString().split('.')[1].length;
  return parseFloat(value.toFixed(numberOfFractionalDigits));
}

export interface IThresholdSliderInfo {
  min: number;
  max: number;
  unit: string;
  step: number;
  recommendedValue?: number;
  domain?: string[];
}

@Component({
  selector: 'slider',
  templateUrl: './slider.component.html',
  styleUrls: ['./slider.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SliderComponent),
      multi: true,
    },
  ],
})
export class SliderComponent implements OnInit, OnChanges, ControlValueAccessor {
  @Input() public unit: string = '';
  @Input() public min: number;
  @Input() public max: number;
  @Input() public step: number;
  @Input() public recommendedValue: number; // this shows a small vertical bar on slider
  @Input() public value: number;
  @Input() public isGradientSlider: boolean;
  @Input() public isDisabled: boolean;

  /**
   * Marks Fractional digits should be used when min and max values are a combination
   * of Integer and Float values. This will ensure that the slider marks are aligned correctly.
   *
   * Typical use case: When min is 7.5 and max is 11, this.marks will be { 7.5: '7.5', 11: '11' }.
   * When these marks are converted into the DOM elements, the max mark will be rendered before the min mark
   * in the HTML tree, causing it to be rendered outside the slider width. Because object (this.marks) iteration
   * doesn't guarantee the order of the keys.
   *
   * So to fix this, we can use marksFractionDigits to convert them into strings using toFixed
   * to guarantee the order of the keys. Default value is 0 (false).
   */
  @Input() public marksFractionDigits: number = 0;

  public isRecommendedValueInvalid: boolean;
  public marks: NzMarks;
  public tooltipPlacement: string;
  private onChange: (_: number) => void;
  private onTouched: () => void;
  @ViewChild('slider', { static: true }) private readonly sliderRef: NzSliderComponent;
  @ViewChild('recommendedValueBar', { static: true }) private readonly recommendedValueBar: ElementRef<HTMLDivElement>;

  public ngOnInit(): void {
    this.initSlider();
  }

  // NzMarks do not re-render by default when input values change
  // hence, needed to update marks obj
  public ngOnChanges(changes: SimpleChanges): void {
    const { unit } = changes;
    if (unit && unit.currentValue && (unit.currentValue !== unit.previousValue)) {
      this.initSlider();
    }
  }

  public onSliderValueChange(value: number): void {
    this.onChange(value);
    this.onTouched();
  }

  public writeValue(value: number): void {
    this.value = roundWithRespectToSliderStep(value, this.step);
  }

  public registerOnChange(fn: () => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  public formatter = (value: number): string => {
    return `${value} ${this.unit}`;
  }

  @HostListener('window:resize')
  private onResize(): void {
    this.setPositionOfRecommendedBar();
  }

  private initSlider(): void {
    this.min = roundWithRespectToSliderStep(this.min, this.step);
    this.max = roundWithRespectToSliderStep(this.max, this.step);
    this.recommendedValue = roundWithRespectToSliderStep(this.recommendedValue, this.step);
    this.setPositionOfRecommendedBar();

    const minMark: number | string =
      this.marksFractionDigits ? this.min.toFixed(this.marksFractionDigits) : this.min;
    const maxMark: number | string =
      this.marksFractionDigits ? this.max.toFixed(this.marksFractionDigits) : this.max;

    this.marks = {
      [minMark]: `${minMark} ${this.unit}`,
      [maxMark]: `${maxMark} ${this.unit}`,
    };
  }

  private setPositionOfRecommendedBar(): void {
    this.isRecommendedValueInvalid = !this.recommendedValue ||
      this.recommendedValue < this.min || this.recommendedValue > this.max;

    if (this.isRecommendedValueInvalid) return;

    const sliderElement: HTMLDivElement = this.sliderRef.slider.nativeElement;
    const interval: number = this.max - this.min;
    const totalSteps: number = interval / this.step;
    const stepWidth: number = sliderElement.clientWidth / totalSteps;

    // Subtracting min from recommended value gives us a number signifying
    // how far recommended bar will be from the min point on slider to adjust left offset
    const markerLeftOffset: number = ((this.recommendedValue - this.min) / this.step) * stepWidth;
    this.recommendedValueBar.nativeElement.style.left = `${markerLeftOffset}px`;
  }
}
